import { Injectable } from '@angular/core';
import { StorageService } from '@shared';
import { ViewStatusEnum } from './view-status';
@Injectable({
  providedIn: 'root'
})
export class ViewConfigurationService {
  private PARENT_TABLE_VIEW = 'PARENT_TABLE_VIEW';
  private CHILD_TABLE_VIEW = 'CHILD_TABLE_VIEW';
  private CONNECTION_ID = 'CONNECTION_ID';
  private AUTH_TOKEN = 'AUTHENTICATION_TOKEN';
  private DEVICE_ID = 'deviceId';
  private userLogged = 'loggedInFusionUserId';
  private APIStatus = 'NO_API';
  private WorkfileTransferOut = 'TransferOutWorkfile';
  private viewStatusEnum = ViewStatusEnum;

  public constructor(
    private storageService: StorageService,
  ) { }

  public async checkViewConfiguration(): Promise<void> {
    try {
      const parentTableView = await this.storageService.get<number>(this.PARENT_TABLE_VIEW);
      if (parentTableView === undefined || parentTableView === null) {
        await this.storageService.set(this.PARENT_TABLE_VIEW, this.viewStatusEnum.Form);
      }
  
      const childTableView = await this.storageService.get<number>(this.CHILD_TABLE_VIEW);
      if (childTableView === undefined || childTableView === null) {
        await this.storageService.set(this.CHILD_TABLE_VIEW, this.viewStatusEnum.Form);
      }
    } catch (error) {
      console.error('Error initializing storage values', error);
    }
  }

  public async updateParentTableView(value: number | null): Promise<void> {
    await this.storageService.set(this.PARENT_TABLE_VIEW, value);
  }

  public async updateChildTableView(value: number | null): Promise<void> {
    await this.storageService.set(this.CHILD_TABLE_VIEW, value);
  }

  public async getParentTableView(): Promise<number | null> {
    return await this.storageService.get<number>(this.PARENT_TABLE_VIEW);
  }
  
  public async getAuth(): Promise<string | null> {
    return await this.storageService.get<string>(this.AUTH_TOKEN);
  }

  public async getChildTableView(): Promise<number | null> {
    return await this.storageService.get<number>(this.CHILD_TABLE_VIEW);
  }
  
  public async getConnectionId(): Promise<string | null> {
    return await this.storageService.get<string>(this.CONNECTION_ID);
  }

  public async setLocalInput(workfileId: string, newValues: string): Promise<void> {
    const key = workfileId + "_localInput";

    const currentValue = await this.storageService.get(key) as string | null; 
    let valueArray: string[] = currentValue ? JSON.parse(currentValue) : [];

    valueArray = valueArray.concat(newValues);

    return await this.storageService.set(key, JSON.stringify(valueArray));
  }

  public async getLocalInput(workfileId: string): Promise<string[]> {
    const key = workfileId + "_localInput";
    const storedValue = await this.storageService.get(key);

    if (typeof storedValue === 'string') {
        try {
            return JSON.parse(storedValue) as string[];
        } catch (error) {
            console.error('Failed to parse stored value:', error);
            return [];
        }
    }
    return [];
  }

  public async setErrorDetail(
    workfileId: string,
    drillHoleName: string,
    newValues: Partial<{ parent: number; child: Record<string, number>[] }>
  ): Promise<void> {
    const key = `${workfileId}_errorDetail`;
  
    const existingData = await this.storageService.get(key);
    let storedData = existingData && typeof existingData === "string" ? JSON.parse(existingData) : {};
  
    if (!storedData[drillHoleName]) {
      storedData[drillHoleName] = { parent: 0, child: [] };
    }

    if (newValues.parent !== undefined) {
      storedData[drillHoleName].parent = Math.max(0, newValues.parent);
    }
  
    if (newValues.child) {
      newValues.child.forEach(newChildEntry => {
        const key = Object.keys(newChildEntry)[0];
        const existingChildIndex = storedData[drillHoleName].child.findIndex(entry => entry.hasOwnProperty(key));
  
        if (existingChildIndex !== -1) {
          storedData[drillHoleName].child[existingChildIndex][key] = Math.max(
            0,
            storedData[drillHoleName].child[existingChildIndex][key] + newChildEntry[key]
          );
        } else {
          storedData[drillHoleName].child.push({ [key]: Math.max(0, newChildEntry[key]) });
        }
      });
    }
  
    await this.storageService.set(key, JSON.stringify(storedData));
  }

  public async getErrorDetail(workfileId: string, drillHoleName: string): Promise<any> {
      const key = `${workfileId}_errorDetail`;

      // Retrieve and parse stored data
      const storedData = await this.storageService.get(key);
      const parsedData = storedData  && typeof storedData === "string" ? JSON.parse(storedData) : {};

      // Return the specific drill hole data or default structure
      return parsedData[drillHoleName] ?? { parent: 0, child: [] };
  }

  public async deleteErrorDetail(workfileId: string, drillHoleName: string): Promise<void> {
    const key = `${workfileId}_errorDetail`;

    // Get existing data
    const existingData = await this.storageService.get(key);
    let storedData = existingData && typeof existingData === "string" ? JSON.parse(existingData) : {};

    // Remove the drill hole entry if it exists
    if (storedData[drillHoleName]) {
        delete storedData[drillHoleName];

        // If no drill holes remain, remove the key entirely
        if (Object.keys(storedData).length === 0) {
            await this.storageService.remove(key);
        } else {
            await this.storageService.set(key, JSON.stringify(storedData));
        }
    }
  }

  public async getDeviceID(): Promise<string | null> {
    return await this.storageService.get<string>(this.DEVICE_ID);
  }

  public async getApiStatus(): Promise<string | null> {
    return await this.storageService.get<string>(this.APIStatus);
  }
  
  public async getTFWorkfile(): Promise<string[] | null> {
    const tfoutWorkfile = await this.storageService.get(this.WorkfileTransferOut);
    if (typeof tfoutWorkfile === 'string') {
        try {
            return JSON.parse(tfoutWorkfile) as string[];
        } catch (error) {
            console.error('Failed to parse stored value:', error);
            return [];
        }
    }
    return [];
  }

  public async setTFWorkfile(newValues: string): Promise<void> {
    const currentValue = await this.storageService.get(this.WorkfileTransferOut) as string | null;
    let valueArray: string[] = currentValue ? JSON.parse(currentValue) : [];

    valueArray = valueArray.concat(newValues);

    return await this.storageService.set(this.WorkfileTransferOut, JSON.stringify(valueArray));
  }

  public async getUserLogged(): Promise<string | null> {
    return await this.storageService.get<string>(this.userLogged);
  }
}
